import React from "react";
import {
  Typography,
  Paper,
  Box,
  Alert,
  Tooltip,
  IconButton,
  Container,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import MemoryIcon from "@mui/icons-material/Memory";
import DeveloperBoardIcon from "@mui/icons-material/DeveloperBoard";
import SdStorageIcon from "@mui/icons-material/SdStorage";
import SpeedIcon from "@mui/icons-material/Speed";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SouthIcon from "@mui/icons-material/South";
import NorthIcon from "@mui/icons-material/North";
import DnsIcon from "@mui/icons-material/Dns";
import color from "../../Constants/colors";
import Loading from "../Common/Loading";
import { useHardwareStatus } from "../../Query/useMonitoring";
import { HardwareCpuCore } from "../../Api/monitoring";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  formatBytes,
  formatRate,
  formatUptime,
  getHealth,
  StatCard,
  DummyBadge,
} from "../SystemMonitoring/monitoringShared";

const CircularGauge: React.FC<{
  title: string;
  percentage: number;
  icon: React.ReactElement;
  subtitle?: string;
}> = ({ title, percentage, icon, subtitle }) => {
  const { t } = useTranslation("monitoring");
  const clamped = Math.max(0, Math.min(100, percentage));
  const health = getHealth(clamped);

  const size = 168;
  const stroke = 14;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - clamped / 100);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: "14px",
        flex: "1 1 260px",
        minWidth: 240,
        backgroundColor: color.primary,
        border: `1px solid ${color.gray200}`,
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        transition: "box-shadow 0.2s, transform 0.2s",
        "&:hover": {
          boxShadow: "0 4px 20px rgba(5,77,127,0.10)",
          transform: "translateY(-2px)",
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          backgroundColor: health.color,
          borderRadius: "14px 14px 0 0",
        }}
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 2,
          alignSelf: "flex-start",
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: `${health.color}14`,
            color: health.color,
          }}
        >
          {icon}
        </Box>
        <Typography
          sx={{
            fontSize: "0.7rem",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            color: color.gray500,
          }}
        >
          {title}
        </Typography>
      </Box>

      <Box sx={{ position: "relative", width: size, height: size }}>
        <svg width={size} height={size}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color.gray100}
            strokeWidth={stroke}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={health.color}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{
              transition: "stroke-dashoffset 0.6s ease, stroke 0.4s ease",
            }}
          />
        </svg>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 800,
              fontSize: "2.25rem",
              lineHeight: 1,
              color: color.textPrimary,
            }}
          >
            {clamped.toFixed(clamped >= 100 ? 0 : 1)}
            <Box
              component="span"
              sx={{ fontSize: "1rem", color: color.gray500, ml: 0.25 }}
            >
              %
            </Box>
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          mt: 2,
          px: 1.5,
          py: 0.5,
          borderRadius: "20px",
          backgroundColor: health.bgColor,
          border: `1px solid ${health.color}30`,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.7rem",
            fontWeight: 700,
            color: health.color,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
          }}
        >
          {t(health.labelKey)}
        </Typography>
      </Box>

      {subtitle && (
        <Typography
          sx={{
            mt: 1.25,
            fontSize: "0.8rem",
            color: color.gray500,
            fontWeight: 600,
            textAlign: "center",
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Paper>
  );
};

const CoreTile: React.FC<{ core: HardwareCpuCore }> = ({ core }) => {
  const { t } = useTranslation("monitoring");
  const clamped = Math.max(0, Math.min(100, core.usage_percent));
  const health = getHealth(clamped);

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: "10px",
        backgroundColor: color.gray50,
        border: `1px solid ${color.gray200}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          mb: 0.75,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.65rem",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            color: color.gray500,
          }}
        >
          {t("coreLabel", { core: core.core })}
        </Typography>
        <Typography
          sx={{
            fontFamily: "'Montserrat', sans-serif",
            fontWeight: 800,
            fontSize: "0.9rem",
            color: health.color,
          }}
        >
          {clamped.toFixed(0)}%
        </Typography>
      </Box>
      <Box
        sx={{
          width: "100%",
          height: 8,
          backgroundColor: color.gray100,
          borderRadius: "4px",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: `${clamped}%`,
            height: "100%",
            backgroundColor: health.color,
            borderRadius: "4px",
            transition: "width 0.6s ease, background-color 0.4s ease",
          }}
        />
      </Box>
    </Box>
  );
};

const PerCoreCpu: React.FC<{ cores: HardwareCpuCore[] }> = ({ cores }) => {
  const { t } = useTranslation("monitoring");

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: "14px",
        backgroundColor: color.primary,
        border: `1px solid ${color.gray200}`,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <DeveloperBoardIcon
          sx={{ color: color.secondary, fontSize: "1.2rem" }}
        />
        <Typography
          sx={{
            fontFamily: "'Montserrat', sans-serif",
            fontWeight: 700,
            fontSize: "1rem",
            color: color.textPrimary,
          }}
        >
          {t("perCoreUsage")}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)",
            sm: "repeat(3, 1fr)",
            md: "repeat(4, 1fr)",
          },
          gap: 1.5,
        }}
      >
        {cores.map((core) => (
          <CoreTile key={core.core} core={core} />
        ))}
      </Box>
    </Paper>
  );
};

const HardwareMonitoring: React.FC = () => {
  const { data, isLoading, isError, error, dataUpdatedAt } =
    useHardwareStatus();
  const queryClient = useQueryClient();
  const { t } = useTranslation("monitoring");

  if (isLoading) {
    return (
      <Loading
        backgroundColor={color.gray100}
        color1={color.accent}
        color2={color.secondary}
      />
    );
  }

  const lastUpdated = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString()
    : "";

  const isDummy = Boolean(data?.is_dummy);

  const header = (
    <Box sx={{ textAlign: "center", mb: 4 }}>
      {isDummy && <DummyBadge />}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        <Typography sx={{ fontSize: "0.75rem", color: color.gray500 }}>
          {t("hardwareAutoRefresh", { time: lastUpdated })}
        </Typography>
        <Tooltip title={t("refreshNow")}>
          <IconButton
            size="small"
            onClick={() =>
              queryClient.invalidateQueries({ queryKey: ["hardwareStatus"] })
            }
            sx={{
              color: color.secondary,
              "&:hover": { color: color.accent },
            }}
          >
            <RefreshIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  if (isError) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="error">
          {t("hardwareFailedToLoad", {
            message: (error as Error)?.message || t("unknownError"),
          })}
        </Alert>
      </Container>
    );
  }

  if (!data) return null;

  if (!data.available) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {header}
        <Alert severity="warning">
          {t("hardwareUnavailable")}
          {data.reason ? ` (${data.reason})` : ""}
        </Alert>
      </Container>
    );
  }

  const cpu = data.cpu;
  const memory = data.memory;
  const disk = data.disk;
  const load = data.load;
  const network = data.network;

  const loadPercent =
    load && load.cores > 0 ? (load.load1 / load.cores) * 100 : 0;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {header}

      <Box
        sx={{
          display: "flex",
          gap: 3,
          justifyContent: "center",
          flexWrap: "wrap",
          mb: 4,
        }}
      >
        <CircularGauge
          title={t("cpuUsage")}
          percentage={cpu?.usage_percent ?? 0}
          icon={<DeveloperBoardIcon />}
          subtitle={cpu ? t("coresCount", { count: cpu.cores }) : undefined}
        />
        <CircularGauge
          title={t("memoryUsage")}
          percentage={memory?.usage_percent ?? 0}
          icon={<MemoryIcon />}
          subtitle={
            memory
              ? `${formatBytes(memory.used_bytes)} / ${formatBytes(memory.total_bytes)}`
              : t("notAvailableShort")
          }
        />
        <CircularGauge
          title={t("diskUsage")}
          percentage={disk?.usage_percent ?? 0}
          icon={<SdStorageIcon />}
          subtitle={
            disk
              ? `${formatBytes(disk.used_bytes)} / ${formatBytes(disk.total_bytes)}`
              : t("notAvailableShort")
          }
        />
      </Box>

      {cpu?.per_core && cpu.per_core.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <PerCoreCpu cores={cpu.per_core} />
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          gap: 2.5,
          justifyContent: "center",
          flexWrap: "wrap",
        }}
      >
        <StatCard
          title={t("loadAverage")}
          value={
            load
              ? `${load.load1.toFixed(2)} / ${load.load5?.toFixed(2) ?? "—"} / ${load.load15?.toFixed(2) ?? "—"}`
              : "—"
          }
          icon={<SpeedIcon />}
          accentColor={getHealth(loadPercent).color}
          hint={t("loadAverageHint")}
        />
        <StatCard
          title={t("uptime")}
          value={formatUptime(data.uptime_seconds)}
          icon={<AccessTimeIcon />}
          accentColor={color.secondary}
        />
        <StatCard
          title={t("networkIn")}
          value={formatRate(network?.receive_rate_bps)}
          icon={<SouthIcon />}
          accentColor={color.green700}
          hint={
            network
              ? t("networkTotal", {
                  total: formatBytes(network.receive_bytes_total),
                })
              : undefined
          }
        />
        <StatCard
          title={t("networkOut")}
          value={formatRate(network?.transmit_rate_bps)}
          icon={<NorthIcon />}
          accentColor={color.accent}
          hint={
            network
              ? t("networkTotal", {
                  total: formatBytes(network.transmit_bytes_total),
                })
              : undefined
          }
        />
        <StatCard
          title={t("cpuCores")}
          value={cpu?.cores ?? "—"}
          icon={<DnsIcon />}
          accentColor={color.blue700}
          hint={disk ? t("diskMount", { mount: disk.mountpoint }) : undefined}
        />
      </Box>
    </Container>
  );
};

export default HardwareMonitoring;
